import { useCallback, useRef, useState } from 'react';
import {
  TABLE_COL_MIN,
  adjustAdjacentTableWidths,
  normalizeColumnWidths,
} from '../utils/tableCellBlocks';

const ACTION_COL_SEL = '.table-add-col-cell';

const dataHeaderCells = (tableEl) => {
  const headerRow = tableEl?.querySelector('thead tr');
  if (!headerRow) return [];
  return [...headerRow.children].filter((th) => !th.matches(ACTION_COL_SEL));
};

const measureColWidths = (tableEl, colCount) => {
  const cells = dataHeaderCells(tableEl);
  if (cells.length < colCount) return null;
  return cells.slice(0, colCount).map((th) =>
    Math.max(1, Math.round(th.getBoundingClientRect().width))
  );
};

const paintColWidths = (tableEl, widths) => {
  if (!tableEl || !widths?.length) return;
  const sum = widths.reduce((a, b) => a + b, 0);
  if (sum <= 0) return;
  const cells = dataHeaderCells(tableEl);
  const cols = tableEl.querySelectorAll('colgroup col:not(.table-action-col)');
  widths.forEach((w, i) => {
    const pct = `${(w / sum) * 100}%`;
    if (cells[i]) cells[i].style.width = pct;
    if (cols[i]) cols[i].style.width = pct;
  });
};

/**
 * Drag the divider between two columns. Width stays inside the table —
 * the left column grows, the right one shrinks.
 */
export const useTableColumnResize = ({
  tableRef,
  colCount,
  onLive,
  onCommit,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef(null);
  const onLiveRef = useRef(onLive);
  const onCommitRef = useRef(onCommit);
  onLiveRef.current = onLive;
  onCommitRef.current = onCommit;

  const startResize = useCallback(
    (colIndex, e) => {
      if (e.button != null && e.button !== 0) return;
      if (colIndex < 0 || colIndex >= colCount - 1) return;
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget?.setPointerCapture?.(e.pointerId);

      const startWidths = measureColWidths(tableRef.current, colCount);
      if (!startWidths) return;

      dragRef.current = { colIndex, startX: e.clientX, startWidths, current: startWidths };
      const prevCursor = document.body.style.cursor;
      const prevSelect = document.body.style.userSelect;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      setIsResizing(true);
      paintColWidths(tableRef.current, startWidths);
      onLiveRef.current(startWidths);

      const onMove = (ev) => {
        const drag = dragRef.current;
        if (!drag) return;
        const floor = Math.max(
          32,
          Math.min(TABLE_COL_MIN, ...drag.startWidths)
        );
        const next = adjustAdjacentTableWidths(
          drag.startWidths,
          drag.colIndex,
          ev.clientX - drag.startX,
          floor
        );
        drag.current = next;
        paintColWidths(tableRef.current, next);
        onLiveRef.current(next);
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevSelect;
        const latest = dragRef.current?.current;
        dragRef.current = null;
        setIsResizing(false);
        if (latest) onCommitRef.current(normalizeColumnWidths(latest, colCount));
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [colCount, tableRef]
  );

  return { startResize, isResizing };
};
